// src/context/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType, RegisterData } from '../types/auth.types';
import { authAPI } from '../api/auth.api.ts';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPendingCancellation, setIsPendingCancellation] = useState<boolean>(
    localStorage.getItem('isPendingCancellation') === 'true'
  );
  const [daysRemaining, setDaysRemaining] = useState<number | null>(
    localStorage.getItem('daysRemaining') ? parseInt(localStorage.getItem('daysRemaining')!, 10) : null
  );

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Verify token is still valid
          const isValid = await authAPI.verifyToken();
          if (isValid) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            // Restore cancellation status from storage
            setIsPendingCancellation(localStorage.getItem('isPendingCancellation') === 'true');
            const storedDays = localStorage.getItem('daysRemaining');
            setDaysRemaining(storedDays ? parseInt(storedDays, 10) : null);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('isPendingCancellation');
            localStorage.removeItem('daysRemaining');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isPendingCancellation');
          localStorage.removeItem('daysRemaining');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Login user
   */
  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.login({ email, password });

      // Transform backend user format (user_id) to frontend format (userId)
      const transformedUser: User = {
        userId: response.user.user_id,
        username: response.user.username,
        email: response.user.email,
        displayName: response.user.display_name,
        avatarUrl: response.user.avatar_url,
        createdAt: response.user.created_at || new Date().toISOString()
      };

      // Store token and user
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(transformedUser));

      // Store cancellation status if present
      if (response.isPendingCancellation) {
        localStorage.setItem('isPendingCancellation', 'true');
        localStorage.setItem('daysRemaining', response.daysRemaining?.toString() || '7');
        setIsPendingCancellation(true);
        setDaysRemaining(response.daysRemaining ?? 7);
      } else {
        localStorage.removeItem('isPendingCancellation');
        localStorage.removeItem('daysRemaining');
        setIsPendingCancellation(false);
        setDaysRemaining(null);
      }

      setToken(response.token);
      setUser(transformedUser);
    } catch (error: any) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (data: RegisterData) => {
    try {
      setError(null);
      setLoading(true);

      // Validate passwords match
      if (data.password !== data.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const response = await authAPI.register(data);

      // Transform backend user format (user_id) to frontend format (userId)
      const transformedUser: User = {
        userId: response.user.user_id,
        username: response.user.username,
        email: response.user.email,
        displayName: response.user.display_name,
        avatarUrl: response.user.avatar_url,
        createdAt: response.user.created_at || new Date().toISOString()
      };

      // Auto-login after registration
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(transformedUser));

      setToken(response.token);
      setUser(transformedUser);
    } catch (error: any) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = () => {
    authAPI.logout();
    localStorage.removeItem('isPendingCancellation');
    localStorage.removeItem('daysRemaining');
    setToken(null);
    setUser(null);
    setError(null);
    setIsPendingCancellation(false);
    setDaysRemaining(null);
  };

  /**
   * Clear cancellation status (called after successful reactivation)
   */
  const clearCancellationStatus = () => {
    localStorage.removeItem('isPendingCancellation');
    localStorage.removeItem('daysRemaining');
    setIsPendingCancellation(false);
    setDaysRemaining(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    loading,
    login,
    register,
    logout,
    error,
    isPendingCancellation,
    daysRemaining,
    clearCancellationStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};